import React, { useCallback, useMemo } from 'react';

import { MasterDataFeaturesList } from 'common/enums/featuresList.enum';
import { FeatureActions } from 'common/models/roles-and-access/roleAccess.model';
import { useRolesAndAccess } from 'common/providers/AccessProvider';
import {
    CategoryFeatureAccess,
    IndicatorFeatureAccess,
    MasterDataFeatureAccess,
    SubCategoryFeatureAccess
} from '../models/master-data-role-access.model';

export interface UseMasterDataAccess {
    masterDataFeatureAccess: MasterDataFeatureAccess;
    categoryFeatureAccess: CategoryFeatureAccess;
    subCategoryFeatureAccess: SubCategoryFeatureAccess;
    indicatorFeatureAccess: IndicatorFeatureAccess;
}

function useMasterDataAccess() {
    const { featuresActions } = useRolesAndAccess();

    //MasterData Actions
    const mapMasterDataToGBBPublic = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.MasterData.MAP_TO_GBBPUBLIC
            ),
        [featuresActions]
    );
    const archiveMasterData = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.MasterData.ARCHIVE_MASTER_DATA
            ),
        [featuresActions]
    );
    const restoreArchivedMasterData = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.MasterData.RESTORE_ARCHIVED_ITEMS
            ),
        [featuresActions]
    );
    const deleteArchivedMasterData = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.MasterData.DELETE_ARCHIVED_ITEMS
            ),
        [featuresActions]
    );

    const exportDataSet = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.MasterData.EXPORT_DATA_SET
            ),
        [featuresActions]
    );

    const getMapMasterDataToGBBPublicDisabled = useCallback(
        (mapMasterDataToGBBPublic: FeatureActions) => {
            if (!mapMasterDataToGBBPublic.read && !mapMasterDataToGBBPublic.write) {
                return true;
            }
            return mapMasterDataToGBBPublic.read != mapMasterDataToGBBPublic.write;
        },
        []
    );

    const getArchiveMasterDataDisabled = useCallback((archiveMasterData: FeatureActions) => {
        if (!archiveMasterData.read && !archiveMasterData.write) {
            return true;
        }
        return archiveMasterData.read != archiveMasterData.write;
    }, []);

    const getRestoreArchivedMasterDataDisabled = useCallback(
        (restoreArchivedMasterData: FeatureActions) => {
            if (!restoreArchivedMasterData.read && !restoreArchivedMasterData.write) {
                return true;
            }
            return restoreArchivedMasterData.read != restoreArchivedMasterData.write;
        },
        []
    );

    const getDeleteArchivedMasterDataDisabled = useCallback(
        (deleteArchivedMasterData: FeatureActions) => {
            if (!deleteArchivedMasterData.read && !deleteArchivedMasterData.write) {
                return true;
            }
            return deleteArchivedMasterData.read != deleteArchivedMasterData.write;
        },
        []
    );

    const getExportMasterDataDisabled = useCallback((exportDataSet: FeatureActions) => {
        if (!exportDataSet.read && !exportDataSet.write) {
            return true;
        }
        return exportDataSet.read != exportDataSet.write;
    }, []);

    //Category Actions
    const addNewCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Category.ADD_CATEGORY
            ),
        [featuresActions]
    );
    const editCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Category.EDIT_CATEGORY
            ),
        [featuresActions]
    );
    const deleteCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Category.DELETE_CATEGORY
            ),
        [featuresActions]
    );
    const archiveCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Category.ARCHIVE_CATEGORY
            ),
        [featuresActions]
    );
    const cloneCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Category.CLONE_CATEGORY
            ),
        [featuresActions]
    );
    const exportCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Category.EXPORT_CATEGORY
            ),
        [featuresActions]
    );
    const getAddNewCategoryDisabled = useCallback((addNewCategory: FeatureActions) => {
        if (!addNewCategory.read && !addNewCategory.write) {
            return true;
        }
        return addNewCategory.read != addNewCategory.write;
    }, []);

    const getEditCategoryDisabled = useCallback((editCategory: FeatureActions) => {
        if (!editCategory.read && !editCategory.write) {
            return true;
        }
        return editCategory.read != editCategory.write;
    }, []);

    const getDeleteCategoryDisabled = useCallback((deleteCategory: FeatureActions) => {
        if (!deleteCategory.read && !deleteCategory.write) {
            return true;
        }
        return deleteCategory.read != deleteCategory.write;
    }, []);

    const getArchiveCategoryDisabled = useCallback((archiveCategory: FeatureActions) => {
        if (!archiveCategory.read && !archiveCategory.write) {
            return true;
        }
        return archiveCategory.read != archiveCategory.write;
    }, []);

    const getCloneCategoryDisabled = useCallback((cloneCategory: FeatureActions) => {
        if (!cloneCategory.read && !cloneCategory.write) {
            return true;
        }
        return cloneCategory.read != cloneCategory.write;
    }, []);

    const getExportCategoryDisabled = useCallback((exportCategory: FeatureActions) => {
        if (!exportCategory.read && !exportCategory.write) {
            return true;
        }
        return exportCategory.read != exportCategory.write;
    }, []);

    //SubCategory
    const addNewSubCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.SubCategory.ADD_SUB_CATEGORY
            ),
        [featuresActions]
    );
    const editSubCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.SubCategory.EDIT_SUB_CATEGORY
            ),
        [featuresActions]
    );
    const deleteSubCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.SubCategory.DELETE_SUB_CATEGORY
            ),
        [featuresActions]
    );
    const archiveSubCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.SubCategory.ARCHIVE_SUB_CATEGORY
            ),
        [featuresActions]
    );
    const moveSubCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.SubCategory.MOVE_SUB_CATEGORY
            ),
        [featuresActions]
    );
    const cloneSubCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.SubCategory.CLONE_SUB_CATEGORY
            ),
        [featuresActions]
    );
    const exportSubCategory = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.SubCategory.EXPORT_SUB_CATEGORY
            ),
        [featuresActions]
    );
    const getAddNewSubCategoryDisabled = useCallback((addNewSubCategory: FeatureActions) => {
        if (!addNewSubCategory.read && !addNewSubCategory.write) {
            return true;
        }
        return addNewSubCategory.read != addNewSubCategory.write;
    }, []);

    const getEditSubCategoryDisabled = useCallback((editSubCategory: FeatureActions) => {
        if (!editSubCategory.read && !editSubCategory.write) {
            return true;
        }
        return editSubCategory.read != editSubCategory.write;
    }, []);

    const getDeleteSubCategoryDisabled = useCallback((deleteSubCategory: FeatureActions) => {
        if (!deleteSubCategory.read && !deleteSubCategory.write) {
            return true;
        }
        return deleteSubCategory.read != deleteSubCategory.write;
    }, []);

    const getArchiveSubCategoryDisabled = useCallback((archiveSubCategory: FeatureActions) => {
        if (!archiveSubCategory.read && !archiveSubCategory.write) {
            return true;
        }
        return archiveSubCategory.read != archiveSubCategory.write;
    }, []);

    const getMoveSubCategoryDisabled = useCallback((moveSubCategory: FeatureActions) => {
        if (!moveSubCategory.read && !moveSubCategory.write) {
            return true;
        }
        return moveSubCategory.read != moveSubCategory.write;
    }, []);

    const getCloneSubCategoryDisabled = useCallback((cloneSubCategory: FeatureActions) => {
        if (!cloneSubCategory.read && !cloneSubCategory.write) {
            return true;
        }
        return cloneSubCategory.read != cloneSubCategory.write;
    }, []);

    const getExportSubCategoryDisabled = useCallback((exportSubCategory: FeatureActions) => {
        if (!exportSubCategory.read && !exportSubCategory.write) {
            return true;
        }
        return exportSubCategory.read != exportSubCategory.write;
    }, []);

    //Indicators
    const addNewIndicator = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Indicator.ADD_INDICATOR
            ),
        [featuresActions]
    );
    const editIndicator = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Indicator.EDIT_INDICATOR
            ),
        [featuresActions]
    );
    const deleteIndicator = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Indicator.DELETE_INDICATOR
            ),
        [featuresActions]
    );
    const archiveIndicator = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Indicator.ARCHIVE_INDICATOR
            ),
        [featuresActions]
    );

    const moveIndicator = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Indicator.MOVE_INDICATOR
            ),
        [featuresActions]
    );
    const linkIndicator = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Indicator.LINK_INDICATOR
            ),
        [featuresActions]
    );
    const copyIndicator = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Indicator.COPY_INDICATOR
            ),
        [featuresActions]
    );

    const exportIndicator = useMemo(
        () =>
            featuresActions.find(
                (ele: FeatureActions) =>
                    ele.featureKey === MasterDataFeaturesList.Indicator.EXPORT_INDICATOR
            ),
        [featuresActions]
    );

    const getAddNewIndicatorDisabled = useCallback((addNewIndicator: FeatureActions) => {
        if (!addNewIndicator.read && !addNewIndicator.write) {
            return true;
        }
        return addNewIndicator.read != addNewIndicator.write;
    }, []);

    const getEditIndicatorDisabled = useCallback((editIndicator: FeatureActions) => {
        if (!editIndicator.read && !editIndicator.write) {
            return true;
        }
        return editIndicator.read != editIndicator.write;
    }, []);

    const getDeleteIndicatorDisabled = useCallback((deleteIndicator: FeatureActions) => {
        if (!deleteIndicator.read && !deleteIndicator.write) {
            return true;
        }
        return deleteIndicator.read != deleteIndicator.write;
    }, []);

    const getArchiveIndicatorDisabled = useCallback((archiveIndicator: FeatureActions) => {
        if (!archiveIndicator.read && !archiveIndicator.write) {
            return true;
        }
        return archiveIndicator.read != archiveIndicator.write;
    }, []);

    const getMoveIndicatorDisabled = useCallback((moveIndicator: FeatureActions) => {
        if (!moveIndicator.read && !moveIndicator.write) {
            return true;
        }
        return moveIndicator.read != moveIndicator.write;
    }, []);

    const getLinkIndicatorDisabled = useCallback((linkIndicator: FeatureActions) => {
        if (!linkIndicator.read && !linkIndicator.write) {
            return true;
        }
        return linkIndicator.read != linkIndicator.write;
    }, []);

    const getCopyIndicatorDisabled = useCallback((copyIndicator: FeatureActions) => {
        if (!copyIndicator.read && !copyIndicator.write) {
            return true;
        }
        return copyIndicator.read != copyIndicator.write;
    }, []);

    const getExportIndicatorDisabled = useCallback((exportIndicator: FeatureActions) => {
        if (!exportIndicator.read && !exportIndicator.write) {
            return true;
        }
        return exportIndicator.read != exportIndicator.write;
    }, []);

    const masterDataFeatureAccess = {
        isMappingToGBBPublicDisabled:
            mapMasterDataToGBBPublic != undefined
                ? getMapMasterDataToGBBPublicDisabled(mapMasterDataToGBBPublic)
                : true,
        isMasterDataArchiveDisabled:
            archiveMasterData != undefined ? getArchiveMasterDataDisabled(archiveMasterData) : true,
        isRestoreArchivedMasterDataDisabled:
            restoreArchivedMasterData != undefined
                ? getRestoreArchivedMasterDataDisabled(restoreArchivedMasterData)
                : true,
        isDeleteArchivedMasterDataDisabled:
            deleteArchivedMasterData != undefined
                ? getDeleteArchivedMasterDataDisabled(deleteArchivedMasterData)
                : true,
        isExportMasterDataDisabled:
            exportDataSet != undefined ? getExportMasterDataDisabled(exportDataSet) : true
    };

    const categoryFeatureAccess = {
        isAddNewCategoryDisabled:
            addNewCategory != undefined ? getAddNewCategoryDisabled(addNewCategory) : true,
        isEditDisabled: editCategory != undefined ? getEditCategoryDisabled(editCategory) : true,
        isDeleteDisabled:
            deleteCategory != undefined ? getDeleteCategoryDisabled(deleteCategory) : true,
        isArchiveDisabled:
            archiveCategory != undefined ? getArchiveCategoryDisabled(archiveCategory) : true,
        isCloneDisabled:
            cloneCategory != undefined ? getCloneCategoryDisabled(cloneCategory) : true,
        isAddNewSubCategoryDisabled:
            addNewSubCategory != undefined ? getAddNewSubCategoryDisabled(addNewSubCategory) : true,
        isExportCategoryDisabled:
            exportCategory != undefined ? getExportCategoryDisabled(exportCategory) : true
    };

    const subCategoryFeatureAccess = {
        isAddNewSubCategoryDisabled:
            addNewSubCategory != undefined ? getAddNewSubCategoryDisabled(addNewSubCategory) : true,
        isEditDisabled:
            editSubCategory != undefined ? getEditSubCategoryDisabled(editSubCategory) : true,
        isDeleteDisabled:
            deleteSubCategory != undefined ? getDeleteSubCategoryDisabled(deleteSubCategory) : true,
        isArchiveDisabled:
            archiveSubCategory != undefined
                ? getArchiveSubCategoryDisabled(archiveSubCategory)
                : true,
        isMoveDisabled:
            moveSubCategory != undefined ? getMoveSubCategoryDisabled(moveSubCategory) : true,
        isCloneDisabled:
            cloneSubCategory != undefined ? getCloneSubCategoryDisabled(cloneSubCategory) : true,
        isAddNewIndicatorDisabled:
            addNewIndicator != undefined ? getAddNewIndicatorDisabled(addNewIndicator) : true,
        isExportSubCategoryDisabled:
            exportSubCategory != undefined ? getExportSubCategoryDisabled(exportSubCategory) : true
    };

    const indicatorFeatureAccess = {
        isEditDisabled: editIndicator != undefined ? getEditIndicatorDisabled(editIndicator) : true,
        isDeleteDisabled:
            deleteIndicator != undefined ? getDeleteIndicatorDisabled(deleteIndicator) : true,
        isArchiveDisabled:
            archiveIndicator != undefined ? getArchiveIndicatorDisabled(archiveIndicator) : true,
        isMoveDisabled: moveIndicator != undefined ? getMoveIndicatorDisabled(moveIndicator) : true,
        isCopyDisabled: copyIndicator != undefined ? getCopyIndicatorDisabled(copyIndicator) : true,
        isLinkDisabled: linkIndicator != undefined ? getLinkIndicatorDisabled(linkIndicator) : true,
        isExportIndicatorDisabled:
            exportIndicator != undefined ? getExportIndicatorDisabled(exportIndicator) : true
    };

    const masterDataFeatureActionList = {
        masterDataFeatureAccess,
        categoryFeatureAccess,
        subCategoryFeatureAccess,
        indicatorFeatureAccess
    };

    return masterDataFeatureActionList;
}

export default useMasterDataAccess;
